'use client';
import React from 'react';
import { CheckCircle2, Clock, AlertCircle, MessageSquare, RefreshCw, FileText, Calendar } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface Props {
  status: 'submitted' | 'under_review' | 'sent_back' | 'approved';
  comment: string;
  onResubmit: () => void;
}

export default function SubmissionStatus({ status, comment, onResubmit }: Props) {
  const statusConfig = {
    submitted: {
      icon: Clock,
      color: 'amber',
      title: 'Submission Received',
      titleEl: 'Η Υποβολή Ελήφθη',
      desc: 'Your thesis has been received and is awaiting librarian review. You will be notified when the review begins.',
      bgClass: 'bg-amber-50 border-amber-200',
      iconClass: 'text-amber-600',
      badgeClass: 'badge-pending',
      badgeLabel: 'Pending Review',
    },
    under_review: {
      icon: FileText,
      color: 'blue',
      title: 'Under Librarian Review',
      titleEl: 'Υπό Εξέταση Βιβλιοθηκονόμου',
      desc: 'A librarian is currently reviewing your thesis metadata and PDF files. This process typically takes 3–5 business days.',
      bgClass: 'bg-blue-50 border-blue-200',
      iconClass: 'text-blue-600',
      badgeClass: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200',
      badgeLabel: 'Under Review',
    },
    sent_back: {
      icon: AlertCircle,
      color: 'red',
      title: 'Sent Back for Revision',
      titleEl: 'Επεστράφη για Αναθεώρηση',
      desc: 'The librarian has reviewed your submission and sent it back with comments. Please review the feedback below and resubmit.',
      bgClass: 'bg-red-50 border-red-200',
      iconClass: 'text-red-600',
      badgeClass: 'badge-sentback',
      badgeLabel: 'Sent Back',
    },
    approved: {
      icon: CheckCircle2,
      color: 'green',
      title: 'Thesis Approved & Published',
      titleEl: 'Η Διατριβή Εγκρίθηκε και Δημοσιεύτηκε',
      desc: 'Congratulations! Your thesis has been approved and is now publicly accessible in the Philips University Repository.',
      bgClass: 'bg-emerald-50 border-emerald-200',
      iconClass: 'text-emerald-600',
      badgeClass: 'badge-approved',
      badgeLabel: 'Published',
    },
  };

  const cfg = statusConfig[status];
  const Icon = cfg.icon;

  // Mock submission data
  const mockSubmission = {
    id: 'sub-2024-0847',
    title: 'Machine Learning Approaches for Predictive Maintenance in Manufacturing Systems',
    submittedDate: '2024-11-14',
    reviewedBy: status === 'sent_back' || status === 'approved' ? 'Librarian M. Georgiou' : null,
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Status card */}
      <div className={`border rounded-2xl p-6 mb-6 ${cfg.bgClass} animate-fade-in`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0`}>
            <Icon size={24} className={cfg.iconClass} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-lg font-700 text-[#0f1a35]">{cfg.title}</h2>
              <span className={cfg.badgeClass}>{cfg.badgeLabel}</span>
            </div>
            <p className="text-sm text-[#4a5578] leading-relaxed mb-1">{cfg.desc}</p>
            <p className="text-xs text-[#8892a8] italic">{cfg.titleEl}</p>
          </div>
        </div>
      </div>

      {/* Submission details */}
      <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden mb-6">
        <div className="bg-[#1a2e5a]/4 border-b border-[#e2e6f0] px-5 py-3">
          <h3 className="text-sm font-700 text-[#0f1a35]">Submission Details</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <FileText size={14} className="text-[#1a2e5a] flex-shrink-0" />
            <div>
              <p className="text-xs text-[#8892a8] font-600 uppercase tracking-wide">Thesis Title</p>
              <p className="text-sm font-600 text-[#0f1a35]">{mockSubmission.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar size={14} className="text-[#1a2e5a] flex-shrink-0" />
            <div>
              <p className="text-xs text-[#8892a8] font-600 uppercase tracking-wide">Submission ID</p>
              <p className="text-sm font-mono-data text-[#0f1a35]">{mockSubmission.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar size={14} className="text-[#1a2e5a] flex-shrink-0" />
            <div>
              <p className="text-xs text-[#8892a8] font-600 uppercase tracking-wide">Date Submitted</p>
              <p className="text-sm font-mono-data text-[#0f1a35]">{mockSubmission.submittedDate}</p>
            </div>
          </div>
          {mockSubmission.reviewedBy && (
            <div className="flex items-center gap-3">
              <MessageSquare size={14} className="text-[#1a2e5a] flex-shrink-0" />
              <div>
                <p className="text-xs text-[#8892a8] font-600 uppercase tracking-wide">Reviewed By</p>
                <p className="text-sm text-[#0f1a35]">{mockSubmission.reviewedBy}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Librarian comments - only when sent back */}
      {status === 'sent_back' && (
        <div className="bg-white border border-red-200 rounded-2xl overflow-hidden mb-6 animate-fade-in">
          <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-center gap-2">
            <MessageSquare size={14} className="text-red-600" />
            <h3 className="text-sm font-700 text-red-800">Librarian Comments / Σχόλια Βιβλιοθηκονόμου</h3>
          </div>
          <div className="p-5">
            <div className="bg-red-50/50 border border-red-100 rounded-xl p-4">
              <p className="text-sm text-[#0f1a35] leading-relaxed">
                {comment || 'The abstract needs to be expanded to at least 200 words. Additionally, please review the selected Elsevier keywords — "Manufacturing Systems" should be replaced with "Industrial Engineering" which is more specific to your research. Please also ensure the ORCID ID is in the correct format (0000-0000-0000-0000).'}
              </p>
            </div>
            <button
              onClick={onResubmit}
              className="mt-4 flex items-center gap-2 btn-navy"
            >
              <RefreshCw size={14} />
              Revise & Resubmit Thesis
            </button>
          </div>
        </div>
      )}

      {/* Approved - link to public */}
      {status === 'approved' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-6 animate-fade-in">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={20} className="text-emerald-600" />
            <div>
              <p className="text-sm font-700 text-emerald-800">Your thesis is now publicly accessible!</p>
              <p className="text-xs text-emerald-700 mt-0.5">It is indexed in the Philips University Repository and can be discovered by researchers worldwide.</p>
            </div>
          </div>
          <Link href="/public-thesis-portal" className="mt-3 inline-flex items-center gap-2 btn-primary text-sm">
            View in Public Repository
          </Link>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
        <div className="bg-[#1a2e5a]/4 border-b border-[#e2e6f0] px-5 py-3">
          <h3 className="text-sm font-700 text-[#0f1a35]">Workflow Timeline</h3>
        </div>
        <div className="p-5">
          <div className="space-y-4">
            {[
              { step: 'Submitted', date: '2024-11-14', done: true },
              { step: 'Under Librarian Review', date: status !== 'submitted' ? '2024-11-16' : null, done: status !== 'submitted' },
              { step: status === 'sent_back' ? 'Sent Back for Revision' : 'Keyword Review & Approval', date: (status === 'sent_back' || status === 'approved') ? '2024-11-19' : null, done: status === 'sent_back' || status === 'approved' },
              { step: 'Published to Repository', date: status === 'approved' ? '2024-11-21' : null, done: status === 'approved' },
            ].map((item, i) => (
              <div key={`timeline-${i}`} className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${item.done ? 'bg-[#1a2e5a] border-[#1a2e5a]' : 'bg-white border-[#e2e6f0]'}`}>
                  {item.done ? <Check size={10} className="text-white" /> : <div className="w-2 h-2 rounded-full bg-[#e2e6f0]" />}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-600 ${item.done ? 'text-[#0f1a35]' : 'text-[#8892a8]'}`}>{item.step}</p>
                  {item.date && <p className="text-xs text-[#8892a8] font-mono-data">{item.date}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Check({ size, className }: { size: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}